import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import UserMenu from './UserMenu';
import ApiService from '../services/api';
import './InterviewersManagement.css';

const InterviewersManagement = ({ user, onSignOut }) => {
  const navigate = useNavigate();
  const [interviewers, setInterviewers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [notification, setNotification] = useState(null);
  const [isSuperUser, setIsSuperUser] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [isAdding, setIsAdding] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedInterviewer, setSelectedInterviewer] = useState(null);
  const [isUpdating, setIsUpdating] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    calendlyLink: '',
    site: ''
  });

  useEffect(() => {
    checkSuperUserAndFetch();
  }, []);

  const checkSuperUserAndFetch = async () => {
    try {
      setLoading(true);
      const superUserStatus = await ApiService.isSuperUser();
      setIsSuperUser(superUserStatus);
      
      if (!superUserStatus) {
        // Redirect non-SuperUsers back to dashboard
        navigate('/dashboard');
        return;
      }

      await fetchInterviewers();
    } catch (err) {
      console.error('Error checking super user status:', err);
      setError('Failed to verify permissions.');
    } finally {
      setLoading(false);
    }
  };

  const fetchInterviewers = async () => {
    try {
      setError(null);
      // Don't pass site parameter to get all interviewers for management
      const data = await ApiService.getAvailableInterviewers();
      setInterviewers(data);
    } catch (err) {
      console.error('Failed to fetch interviewers:', err);
      setError(err.message);
    }
  };

  const showNotification = (message, type = 'success') => {
    setNotification({ message, type });
    setTimeout(() => setNotification(null), 5000);
  };

  const hideNotification = () => {
    setNotification(null);
  };

  const handleAddInterviewer = () => {
    setShowAddModal(true);
  };

  const handleCloseModal = () => {
    setShowAddModal(false);
    setFormData({
      name: '',
      email: '',
      calendlyLink: '',
      site: ''
    });
  };

  const handleFormChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSubmitInterviewer = async (e) => {
    e.preventDefault();
    
    // Basic validation
    if (!formData.name.trim() || !formData.email.trim() || !formData.site.trim()) {
      showNotification('Please fill in all required fields (Name, Email, Site)', 'error');
      return;
    }

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      showNotification('Please enter a valid email address', 'error');
      return;
    }

    try {
      setIsAdding(true);
      await ApiService.addInterviewer(formData);
      showNotification('Interviewer added successfully!', 'success');
      handleCloseModal();
      await fetchInterviewers(); // Refresh the list
    } catch (err) {
      console.error('Failed to add interviewer:', err);
      showNotification(err.message || 'Failed to add interviewer', 'error');
    } finally {
      setIsAdding(false);
    }
  };

  const handleEditInterviewer = (interviewer) => {
    setSelectedInterviewer(interviewer);
    setFormData({
      name: interviewer.name || '',
      email: interviewer.email || '',
      calendlyLink: interviewer.calendly || '',
      site: interviewer.site || interviewer.location || ''
    });
    setShowEditModal(true);
  };

  const handleUpdateInterviewer = async (e) => {
    e.preventDefault();
    
    // Basic validation
    if (!formData.name.trim() || !formData.site.trim()) {
      showNotification('Please fill in all required fields (Name, Site)', 'error');
      return;
    }

    try {
      setIsUpdating(true);
      await ApiService.updateInterviewer(selectedInterviewer.email, formData);
      showNotification('Interviewer updated successfully!', 'success');
      handleCloseEditModal();
      await fetchInterviewers(); // Refresh the list
    } catch (err) {
      console.error('Failed to update interviewer:', err);
      showNotification(err.message || 'Failed to update interviewer', 'error');
    } finally {
      setIsUpdating(false);
    }
  };

  const handleDeleteInterviewer = (interviewer) => {
    setSelectedInterviewer(interviewer);
    setShowDeleteModal(true);
  };

  const handleConfirmDelete = async () => {
    try {
      setIsDeleting(true);
      await ApiService.deleteInterviewer(selectedInterviewer.email);
      showNotification('Interviewer deleted successfully!', 'success');
      handleCloseDeleteModal();
      await fetchInterviewers(); // Refresh the list
    } catch (err) {
      console.error('Failed to delete interviewer:', err);
      showNotification(err.message || 'Failed to delete interviewer', 'error');
    } finally {
      setIsDeleting(false);
    }
  };

  const handleCloseEditModal = () => {
    setShowEditModal(false);
    setSelectedInterviewer(null);
    setFormData({
      name: '',
      email: '',
      calendlyLink: '',
      site: ''
    });
  };

  const handleCloseDeleteModal = () => {
    setShowDeleteModal(false);
    setSelectedInterviewer(null);
  };

  if (loading) {
    return (
      <div className="interviewers-management">
        <div className="loading-container">
          <div className="spinner"></div>
          <p>Loading interviewers...</p>
        </div>
      </div>
    );
  }

  if (!isSuperUser) {
    return null; // This shouldn't render due to redirect, but just in case
  }

  return (
    <div className="dashboard-container">
      {/* Notification */}
      {notification && (
        <div className={`notification ${notification.type}`}>
          <div className="notification-content">
            <span className="notification-message">{notification.message}</span>
            <button className="notification-close" onClick={hideNotification}>
              <svg viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
              </svg>
            </button>
          </div>
        </div>
      )}
      
      <div className="applications-panel">
        <div className="panel-header">
          <div className="panel-title-container">
            <button 
              onClick={() => navigate('/dashboard')} 
              className="back-button"
              aria-label="Back to Dashboard"
            >
              <svg viewBox="0 0 20 20" fill="currentColor" className="back-icon">
                <path fillRule="evenodd" d="M9.707 16.707a1 1 0 01-1.414 0l-6-6a1 1 0 010-1.414l6-6a1 1 0 011.414 1.414L5.414 9H17a1 1 0 110 2H5.414l4.293 4.293a1 1 0 010 1.414z" clipRule="evenodd" />
              </svg>
            </button>
            <h1 className="panel-title">Manage Interviewers</h1>
            <button 
              onClick={handleAddInterviewer}
              className="add-interviewer-button"
            >
              <svg className="add-icon" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z" clipRule="evenodd" />
              </svg>
              Add Interviewer
            </button>
          </div>
          <UserMenu user={user} onSignOut={onSignOut} />
        </div>

        {!loading && (
          <div className="modern-table">
            {error ? (
              <div className="error-state">
                <p>Error: {error}</p>
                <button onClick={fetchInterviewers} className="retry-btn">
                  Retry
                </button>
              </div>
            ) : interviewers.length === 0 ? (
              <div className="empty-state">
                <p>No interviewers found.</p>
              </div>
            ) : (
              <>
                <div className="table-headers">
                  <div className="header-item name-header">Name</div>
                  <div className="header-item email-header">Email</div>
                  <div className="header-item site-header">Site</div>
                  <div className="header-item calendly-header">Calendly Link</div>
                  <div className="header-item actions-header">Actions</div>
                </div>
                
                <div className="table-content">
                  {interviewers.map((interviewer, index) => (
                    <div 
                      key={interviewer.email || index} 
                      className="interviewer-row"
                    >
                      <div className="row-item name-item">
                        <span className="interviewer-name">
                          {interviewer.name || 'Unknown'}
                        </span>
                      </div>
                      
                      <div className="row-item email-item">
                        <span className="interviewer-email-text">
                          {interviewer.email}
                        </span>
                      </div>
                      
                      <div className="row-item site-item">
                        <span className="interviewer-site">
                          {interviewer.site || interviewer.location || 'N/A'}
                        </span>
                      </div>
                      
                        <div className="row-item calendly-item">
                          {interviewer.calendly ? (
                            <a 
                              href={interviewer.calendly.startsWith('http://') || interviewer.calendly.startsWith('https://') 
                                ? interviewer.calendly 
                                : `https://${interviewer.calendly}`} 
                              target="_blank" 
                              rel="noopener noreferrer"
                              className="calendly-link"
                              onClick={(e) => e.stopPropagation()}
                            >
                              <svg className="calendly-icon" viewBox="0 0 20 20" fill="currentColor">
                                <path fillRule="evenodd" d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z" clipRule="evenodd" />
                              </svg>
                              Schedule
                            </a>
                          ) : (
                            <span className="no-calendly">No link</span>
                          )}
                        </div>
                        
                        <div className="row-item actions-item">
                          <div className="action-buttons">
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleEditInterviewer(interviewer);
                              }}
                              className="edit-button"
                              title="Edit interviewer"
                            >
                              <svg viewBox="0 0 20 20" fill="currentColor" className="edit-icon">
                                <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" />
                              </svg>
                            </button>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDeleteInterviewer(interviewer);
                              }}
                              className="delete-button"
                              title="Delete interviewer"
                            >
                              <svg viewBox="0 0 20 20" fill="currentColor" className="delete-icon">
                                <path fillRule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z" clipRule="evenodd" />
                              </svg>
                            </button>
                          </div>
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        )}
      </div>

      {/* Add Interviewer Modal */}
      {showAddModal && (
        <div className="modal-overlay" onClick={handleCloseModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Add New Interviewer</h2>
              <button onClick={handleCloseModal} className="modal-close">
                <svg viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                </svg>
              </button>
            </div>
            
            <form onSubmit={handleSubmitInterviewer} className="modal-form">
              <div className="form-group">
                <label htmlFor="name">Name *</label>
                <input
                  type="text"
                  id="name"
                  value={formData.name}
                  onChange={(e) => handleFormChange('name', e.target.value)}
                  placeholder="Enter interviewer's full name"
                  required
                />
              </div>
              
              <div className="form-group">
                <label htmlFor="email">Email *</label>
                <input
                  type="email"
                  id="email"
                  value={formData.email}
                  onChange={(e) => handleFormChange('email', e.target.value)}
                  placeholder="Enter interviewer's email address"
                  required
                />
              </div>
              
              <div className="form-group">
                <label htmlFor="site">Site *</label>
                <select
                  id="site"
                  value={formData.site}
                  onChange={(e) => handleFormChange('site', e.target.value)}
                  required
                >
                  <option value="">Select a site</option>
                  <option value="Berlin">Berlin</option>
                  <option value="Cambridge">Cambridge</option>
                  <option value="San Francisco">San Francisco</option>
                  <option value="Tokyo">Tokyo</option>
                </select>
              </div>
              
              <div className="form-group">
                <label htmlFor="calendlyLink">Calendly Link</label>
                <input
                  type="url"
                  id="calendlyLink"
                  value={formData.calendlyLink}
                  onChange={(e) => handleFormChange('calendlyLink', e.target.value)}
                  placeholder="Enter Calendly scheduling link (optional)"
                />
              </div>
              
              <div className="modal-actions">
                <button 
                  type="button" 
                  onClick={handleCloseModal}
                  className="cancel-button"
                  disabled={isAdding}
                >
                  Cancel
                </button>
                <button 
                  type="submit" 
                  className="submit-button"
                  disabled={isAdding}
                >
                  {isAdding ? (
                    <>
                      <div className="button-spinner"></div>
                      Adding...
                    </>
                  ) : (
                    'Add Interviewer'
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Interviewer Modal */}
      {showEditModal && selectedInterviewer && (
        <div className="modal-overlay" onClick={handleCloseEditModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Edit Interviewer</h2>
              <button onClick={handleCloseEditModal} className="modal-close">
                <svg viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                </svg>
              </button>
            </div>
            
            <form onSubmit={handleUpdateInterviewer} className="modal-form">
              <div className="form-group">
                <label htmlFor="edit-name">Name *</label>
                <input
                  type="text"
                  id="edit-name"
                  value={formData.name}
                  onChange={(e) => handleFormChange('name', e.target.value)}
                  placeholder="Enter interviewer's full name"
                  required
                />
              </div>
              
              <div className="form-group">
                <label htmlFor="edit-email">Email</label>
                <input
                  type="email"
                  id="edit-email"
                  value={formData.email}
                  disabled
                  className="disabled-input"
                  title="Email cannot be changed"
                />
                <small className="form-note">Email cannot be changed</small>
              </div>
              
              <div className="form-group">
                <label htmlFor="edit-site">Site *</label>
                <select
                  id="edit-site"
                  value={formData.site}
                  onChange={(e) => handleFormChange('site', e.target.value)}
                  required
                >
                  <option value="">Select a site</option>
                  <option value="Berlin">Berlin</option>
                  <option value="Cambridge">Cambridge</option>
                  <option value="San Francisco">San Francisco</option>
                  <option value="Tokyo">Tokyo</option>
                </select>
              </div>
              
              <div className="form-group">
                <label htmlFor="edit-calendlyLink">Calendly Link</label>
                <input
                  type="url"
                  id="edit-calendlyLink"
                  value={formData.calendlyLink}
                  onChange={(e) => handleFormChange('calendlyLink', e.target.value)}
                  placeholder="Enter Calendly scheduling link (optional)"
                />
              </div>
              
              <div className="modal-actions">
                <button 
                  type="button" 
                  onClick={handleCloseEditModal}
                  className="cancel-button"
                  disabled={isUpdating}
                >
                  Cancel
                </button>
                <button 
                  type="submit" 
                  className="submit-button"
                  disabled={isUpdating}
                >
                  {isUpdating ? (
                    <>
                      <div className="button-spinner"></div>
                      Updating...
                    </>
                  ) : (
                    'Update Interviewer'
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && selectedInterviewer && (
        <div className="modal-overlay" onClick={handleCloseDeleteModal}>
          <div className="modal-content delete-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Delete Interviewer</h2>
              <button onClick={handleCloseDeleteModal} className="modal-close">
                <svg viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                </svg>
              </button>
            </div>
            
            <div className="delete-confirmation">
              <div className="delete-icon-wrapper">
                <svg className="delete-warning-icon" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
              </div>
              <h3>Are you sure you want to delete this interviewer?</h3>
              <div className="interviewer-details">
                <p><strong>Name:</strong> {selectedInterviewer.name}</p>
                <p><strong>Email:</strong> {selectedInterviewer.email}</p>
                <p><strong>Site:</strong> {selectedInterviewer.site || selectedInterviewer.location || 'N/A'}</p>
              </div>
              <p className="delete-warning">This action cannot be undone.</p>
            </div>
            
            <div className="modal-actions">
              <button 
                type="button" 
                onClick={handleCloseDeleteModal}
                className="cancel-button"
                disabled={isDeleting}
              >
                Cancel
              </button>
              <button 
                onClick={handleConfirmDelete}
                className="delete-confirm-button"
                disabled={isDeleting}
              >
                {isDeleting ? (
                  <>
                    <div className="button-spinner"></div>
                    Deleting...
                  </>
                ) : (
                  'Delete Interviewer'
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default InterviewersManagement;
